import { db } from '../firebase';
import { collection, doc, getDoc, getFirestore } from 'firebase/firestore';

/**
 * Fetch all properties from the Firestore database
 * @returns {Promise<Object>} A promise that resolves to an object containing all properties
 */
export const getAllProperties = async () => {
  try {
    const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
    // Use cached data first, then network
    const docSnap = await getDoc(propertiesDocRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return {};
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Get a property by its property ID
 * @param {string} propertyId - The ID of the property to fetch
 * @returns {Promise<Object|null>} A promise that resolves to the property object or null if not found
 */
export const getPropertyById = async (propertyId) => {
  try {
    const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
    const docSnap = await getDoc(propertiesDocRef);
    
    if (docSnap.exists()) {
      const propertiesData = docSnap.data();
      return propertiesData[propertyId] || null;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Get properties filtered by location
 * @param {string} location - The location to filter by
 * @returns {Promise<Array>} A promise that resolves to an array of properties
 */
export const getPropertiesByLocation = async (location) => {
  try {
    const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
    const docSnap = await getDoc(propertiesDocRef);
    
    if (docSnap.exists()) {
      const propertiesData = docSnap.data();
      const filteredProperties = [];
      
      for (const [id, property] of Object.entries(propertiesData)) {
        if (property.location && property.location.toLowerCase().includes(location.toLowerCase())) {
          filteredProperties.push({
            id,
            ...property
          });
        }
      }
      
      return filteredProperties;
    } else {
      return [];
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Convert properties object to array with ID included
 * @param {Object} propertiesObj - The properties object from Firestore
 * @returns {Array} An array of properties with their IDs
 */
export const propertiesToArray = (propertiesObj) => {
  return Object.entries(propertiesObj || {}).map(([id, property]) => ({
    id,
    ...property
  }));
};
